import React, { useRef, useState, useEffect } from 'react';
import { motion, useInView } from 'framer-motion';

const highlights = [
  'Real-time transcription',
  'Speaker identification',
  'AI question detection',
  'One-click summaries',
];

function Demo() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const [media, setMedia] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch available media files dynamically
  useEffect(() => {
    fetch('/api/media')
      .then(res => res.json())
      .then(data => {
        if (data.files && data.files.length > 0) {
          // Use first available media file
          setMedia(data.files[0]);
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  return (
    <section id="demo" className="snap-section relative min-h-screen flex items-center py-20 overflow-hidden">
      {/* Background glows */}
      <div className="absolute top-1/4 -left-32 w-96 h-96 bg-accent-cyan/20 rounded-full blur-[150px]" />
      <div className="absolute bottom-1/4 -right-32 w-96 h-96 bg-accent-magenta/20 rounded-full blur-[150px]" />

      <div className="max-w-[1800px] mx-auto px-6 md:px-12 w-full relative z-10">
        <motion.div
          ref={ref}
          className="text-center mb-12"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <p className="section-label">See It In Action</p>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold leading-tight">
            Watch NaLiVox{' '}
            <span className="gradient-text">in action</span>
          </h2>
        </motion.div>

        {/* Demo container */}
        <motion.div
          className="relative max-w-5xl mx-auto"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={isInView ? { opacity: 1, scale: 1 } : {}}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          {/* Gradient border frame */}
          <div className="gradient-border rounded-2xl p-1">
            <div className="bg-dark rounded-xl overflow-hidden">
              {/* Browser bar */}
              <div className="bg-dark-secondary px-4 py-3 flex items-center gap-2 border-b border-white/5">
                <div className="flex gap-2">
                  <div className="w-3 h-3 rounded-full bg-red-500/80" />
                  <div className="w-3 h-3 rounded-full bg-yellow-500/80" />
                  <div className="w-3 h-3 rounded-full bg-green-500/80" />
                </div>
                <div className="flex-1 mx-4">
                  <div className="bg-white/5 rounded-full px-4 py-1 text-xs text-gray-500 max-w-md mx-auto">
                    localhost:8080/app
                  </div>
                </div>
              </div>

              {/* Dynamic Media Display */}
              <div className="relative aspect-video bg-dark">
                {loading ? (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-8 h-8 border-2 border-accent-cyan border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : media ? (
                  media.type === 'video' ? (
                    <video
                      src={media.path}
                      autoPlay
                      loop
                      muted
                      playsInline
                      className="w-full h-full object-contain"
                    />
                  ) : (
                    <img
                      src={media.path}
                      alt="NaLiVox Demo"
                      className="w-full h-full object-contain"
                    />
                  )
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center text-gray-500">
                    <p>Demo coming soon</p>
                  </div>
                )}
                {/* Overlay gradient */}
                <div className="absolute inset-0 bg-gradient-to-t from-dark/50 to-transparent pointer-events-none" />
              </div>
            </div>
          </div>

          {/* Floating badge */}
          <motion.div
            className="absolute -top-4 -right-4 px-4 py-2 bg-accent-cyan text-black text-xs font-bold uppercase tracking-wider rounded-full"
            animate={{ y: [0, -5, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            Live Demo
          </motion.div>
        </motion.div>

        {/* Feature highlights */}
        <motion.div
          className="flex flex-wrap justify-center gap-4 mt-10"
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.5 }}
        >
          {highlights.map((item) => (
            <div
              key={item}
              className="px-4 py-2 rounded-full border border-white/10 text-sm text-gray-400"
            >
              {item}
            </div>
          ))}
        </motion.div>

        {/* CTA */}
        <motion.div
          className="text-center mt-10"
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.7 }}
        >
          <a href="/app" className="btn-primary inline-block">
            Start Transcribing
          </a>
        </motion.div>
      </div>
    </section>
  );
}

export default Demo;
